import fr from 'facet-rest';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import useMobileLayout from 'Hooks/useMobileLayout';
import SurveyStepHeader from 'Views/Onboarding/Components/SurveyStepHeader';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import ClientRiskToleranceValue from 'Components/RiskTolerance/ClientRiskToleranceValue';
import { hasRelationship } from 'Helpers/Affordances';
import ImageAvatar from 'Views/Onboarding/Components/ImageAvatar';
import MoreInfoOverlay from './MoreInfoOverlay';
import onboardingStyles from '../../../../../../Onboarding/Pages/Survey/SurveyStep/StepLibrary/RiskTolerance/riskToleranceManualLayoutStyles';

const styles = theme => ({
  ...onboardingStyles(theme),
  root: {
    width: '100%',
    margin: 'auto',
    display: 'flex',
    maxWidth: '400px',
    alignItems: 'center',
    flexDirection: 'column',
    marginBottom: theme.spacing(10),
    padding: theme.spacing(2),
  },
  rootDesktop: {
    maxWidth: '600px',
    padding: theme.spacing(2),
  },
  content: {
    width: '100%',
  },
  surveyStepHeader: {
    padding: 0,
  },
  surveyStepHeaderTitle: {
    fontWeight: theme.typography.fontWeightBold,
    marginBottom: theme.spacing(4),
  },
  surveyStepHeaderSubtitle: {
    fontSize: theme.typography.h6.fontSize,
    fontWeight: theme.typography.fontWeightBold,
    marginTop: 0,
    marginBottom: theme.spacing(4),
  },
  overlayContentHeading: {
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(3),
    fontSize: theme.typography.h6.fontSize,
  },
  overlayContentBody: {
    color: theme.palette.facet.primaryGray,
    marginBottom: theme.spacing(3),
    textAlign: 'justify',
  },
  riskToleranceValues: {
    width: '100%',
  },
  hr: {
    maxWidth: theme.dimensions.xs,
  },
  imageAvatar: {
    height: theme.spacing(12),
    width: theme.spacing(12),
    margin: `${theme.spacing(5)}px auto`,
  },
});

const Content = ({
  classes,
}) => {
  const {
    resource,
  } = useMetaLayoutContext();

  const isMobile = useMobileLayout();
  const title = resource.attr('title') || resource.attr('primaryText');
  const subtitle = resource.attr('subtitle') || resource.attr('secondaryText');
  const [riskToleranceOptions] = useState(resource.meta().field('clients').attr('arrayDef').template.find(t => t.name === 'riskTolerance'));

  return (
    <div className={classNames(classes.root, {
      [classes.rootDesktop]: !isMobile,
    })}
    >
      <SurveyStepHeader
        title={title}
        subtitle={subtitle}
        titleTest={'wizard-step-title'}
        subtitleTest={'wizard-step-subtitle'}
        classes={{
          root: classes.surveyStepHeader,
          title: classes.surveyStepHeaderTitle,
          subtitle: classes.surveyStepHeaderSubtitle,
        }}
      />
      <Typography
        className={classes.tertiaryText}
        color="primaryGray"
      >
        {resource.attr('tertiaryText')}
      </Typography>
      <div className={classes.riskToleranceValues}>
        {resource.attr('clients')?.resources().list().map((client, i) => (
          <>
            {i > 0 ? (
              <>
                <hr className={classes.hr} />
                {hasRelationship(client, fr.relationships.image.name) ? (
                  <ImageAvatar className={classes.imageAvatar} resource={client} />
                ) : null}
              </>
            ) : null}
            <ClientRiskToleranceValue
              key={client.id()}
              className={classes.client}
              client={client}
              metaOptions={riskToleranceOptions}
            />
          </>

        ))}
      </div>

      <MoreInfoOverlay
        title="Risk Tolerance"
        overlayContent={
          <div>
            <Typography className={classes.overlayContentHeading}>Conservative</Typography>
            <Typography className={classes.overlayContentBody}>Conservative Investors typically prioritize preserving their money over growing it. As a Conservative Investor, you are willing to miss out on bigger gains in order to protect yourself from big losses.</Typography>
            <Typography className={classes.overlayContentHeading}>Moderately Conservative</Typography>
            <Typography className={classes.overlayContentBody}>Moderately Conservative Investors primarily seek stability but still expect some gain in their invested dollars over time. As a Moderately Conservative Investor, you generally avoid investments that cause drastic changes to your portfolio value.</Typography>
            <Typography className={classes.overlayContentHeading}>Moderate</Typography>
            <Typography className={classes.overlayContentBody}>Moderate Investors seek balance and are not comfortable with large swings (up or down) in their invested dollars. As a Moderate Investor, you are willing to handle an average amount of losses for a similar amount of potential gains.</Typography>
            <Typography className={classes.overlayContentHeading}>Moderately Aggressive</Typography>
            <Typography className={classes.overlayContentBody}>Moderately Aggressive Investors seek potentially-outsized gains. As a Moderately Aggressive Investor, you are comfortable taking on a considerable amount of potential loss—often willing to hold onto investments through market ups and downs.</Typography>
            <Typography className={classes.overlayContentHeading}>Aggressive</Typography>
            <Typography className={classes.overlayContentBody}>Aggressive Investors seek to maximize potential gains. As an Aggressive Investor, you are completely comfortable with large swings in the value of your portfolio and have a high tolerance for losses.</Typography>
          </div>
      }
      />
    </div>
  );
};

Content.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(Content);
